import { Suspense, lazy, useState, useSyncExternalStore } from 'react';
import { PausedBanner } from './auth/PausedBanner';
import { Route, Router } from './router';
import HomeRoute from './routes/home/HomeRoute';
import { useSession, wall } from './session';

// The sign-in sheet is its own chunk (Turnstile, passkeys), fetched the first
// time the wall asks for it, never for a visitor who only looks around.
const SignInSheet = lazy(() => import('./auth/SignInSheet').then((m) => ({ default: m.SignInSheet })));
// Settings is one lazy screen at two paths: the index and the pane it opens.
// One component for both, so moving between them on a desk (where the pane
// sits beside the index) keeps the screen mounted rather than loading it again.
const SettingsRoute = lazy(() => import('./routes/settings/SettingsRoute'));

// The route table. Home ships in the shell, since it is the first thing
// seen and a second round trip for it costs the first paint; every other
// screen is a lazy chunk so the shell stays small. The session (who is
// signed in, and sync while they are) and the wall sit beside the router,
// so neither re-renders the screens when someone signs in.
export function App() {
  return (
    <>
      <Session />
      <Router>
        <Route path="/" component={HomeRoute} />
        <Route path="/new" load={() => import('./routes/capture/CaptureRoute')} />
        <Route path="/plan/:id" load={() => import('./routes/plan/PlanRoute')} />
        <Route path="/focus" load={() => import('./routes/focus/FocusRoute')} />
        <Route path="/design" load={() => import('./routes/design/DesignRoute')} />
        <Route path="/signin" load={() => import('./auth/SignInRoute')} />
        <Route path="/print/:id" load={() => import('./print/PrintRoute')} />
        <Route path="/balance" load={() => import('./routes/balance/BalanceRoute')} />
        <Route path="/invite" load={() => import('./routes/invite/InviteRoute')} />
        <Route path="/i/:code" load={() => import('./routes/invite/LandingRoute')} />
        <Route path="/welcome" load={() => import('./onboarding/WelcomeRoute')} />
        <Route path="/legal" load={() => import('./routes/legal/LegalRoute')} />
        <Route path="/legal/:doc" load={() => import('./routes/legal/LegalRoute')} />
        <Route path="/settings" component={SettingsRoute} />
        <Route path="/settings/:pane" component={SettingsRoute} />
      </Router>
      <Wall />
      <PausedBanner />
    </>
  );
}

function Session() {
  useSession();
  return null;
}

/** The sign-up wall: the sign-in sheet, opened by requireAccount() from any screen. */
function Wall() {
  const open = useSyncExternalStore(wall.subscribe, wall.get);
  // Kept once opened, so closing plays out rather than vanishing.
  const [wanted, setWanted] = useState(false);
  if (open && !wanted) setWanted(true);
  if (!wanted) return null;
  return (
    <Suspense fallback={null}>
      <SignInSheet open={open} onClose={wall.closed} onSignedIn={wall.signedIn} />
    </Suspense>
  );
}
